/**
 * `hec_src` GA4 entry-attribution cookie — shared constants + pure value logic
 * (SFCC-12091).
 *
 * The Workers→SFRA `/checkout` handoff is a hard page load: gtag re-inits on a
 * URL with no campaign params and an internal referrer, so GA4 overwrites
 * `session_traffic_source_last_click` to `(not set)` and every checkout event
 * through `purchase` inherits it. This cookie preserves the session's entry
 * attribution (utm_* / ad click ids / external referrer) so GTM can re-supply
 * it as `campaign_*` config params on SFRA-side pageviews. Full contract +
 * martech handoff spec: `docs/hec-src-attribution-cookie.md`.
 *
 * Isomorphic on purpose: the server builder (`hec-src.server.ts`) and the
 * inline client backstop (`hec-src-inline.tsx`) must produce byte-identical
 * values — a parity test executes the shipped snippet against these functions.
 *
 * Wire format: ordered `key=value` pairs joined by `&`, keys omitted when
 * absent. Values are `encodeURIComponent`-encoded — spaces serialize as `%20`,
 * NEVER `+` (the SFCC-12027 lesson: a literal `+` in `utm_medium` breaks GA4
 * channel-grouping string matches). The pair separators stay literal `&`/`=`
 * (legal cookie-octets; readers split on the FIRST `=` after the cookie name).
 */
import { IMAGE_HOSTS } from "~/config/environments";

export const HEC_SRC_COOKIE_NAME = "hec_src";

/**
 * 4-hour rolling window (COM-787). It was 1800 s (GA4's
 * session-inactivity timeout), which aged the cookie out mid-journey on long
 * desktop / Safari sessions before the `/checkout` hard load re-read it. The
 * accepted trade-off: a return visit within 4 h of the last activity
 * re-supplies the stored source to what GA4 counts as a new session.
 */
export const HEC_SRC_MAX_AGE_SECONDS = 14400;

/**
 * Campaign params captured from the landing URL, in wire order. Deliberately
 * NOT shared with the SEO canonical-strip lists (`plp-seo.ts`) — those have
 * different semantics (they strip `fbclid`/`AFID`/`src`, and a future SEO edit
 * must not silently change attribution capture).
 */
export const HEC_SRC_CAMPAIGN_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
] as const;

export type HecSrcCampaignKey = (typeof HEC_SRC_CAMPAIGN_KEYS)[number];

/**
 * Ad click identifiers — the subset of the campaign keys that GA4's Conversion
 * Linker re-decorates onto INTERNAL navigations via `_gl`/URL passthrough
 * (AR-163). Their presence on a URL is therefore NOT evidence of a new ad
 * click, which is the whole of COM-510: treating a `?gclid=…` internal nav as
 * a fresh campaign touch destroyed the stored `utm_*` and sent
 * `session_traffic_source_last_click` to `(not set)` through purchase.
 *
 * Deliberately a subset of `HEC_SRC_CAMPAIGN_KEYS` rather than a separate
 * list, so a key can never be captured without also being classified.
 */
export const HEC_SRC_CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;

/**
 * True when the captured params carry an ad click id and NO `utm_*`.
 *
 * This is the shape a Conversion-Linker-decorated internal nav takes. A real
 * campaign touch carries `utm_*`; a real no-utm ad click arrives with an
 * external referrer, which the caller checks separately (COM-510 chose the
 * ticket's rule: only `utm_*` or a genuine external referrer may REPLACE the
 * stored campaign set).
 */
export function isClickIdOnlyCampaign(fields: HecSrcCampaignFields): boolean {
  const clickIds: readonly string[] = HEC_SRC_CLICK_ID_KEYS;
  let hasClickId = false;
  let hasUtm = false;
  for (const key of HEC_SRC_CAMPAIGN_KEYS) {
    if (!fields[key]) continue;
    if (clickIds.includes(key)) hasClickId = true;
    else hasUtm = true;
  }
  return hasClickId && !hasUtm;
}

/**
 * Fold a click-id-only touch into the stored fields: the new click id wins,
 * every other stored field (`utm_*`, `ref`) survives.
 *
 * `ts` is deliberately the STORED one. It is documented as the epoch-ms of the
 * last VALUE capture, and a Conversion Linker decoration is not a new campaign
 * touch — the acquisition still happened when the utm landed. The rolling
 * window rides on `Max-Age`, which the caller refreshes regardless, so
 * keeping `ts` does not shorten the cookie's life.
 */
export function mergeClickIdIntoStored(
  stored: HecSrcFields,
  clickIdFields: HecSrcCampaignFields,
): HecSrcFields {
  const merged: HecSrcFields = { ...stored };
  for (const key of HEC_SRC_CLICK_ID_KEYS) {
    const value = clickIdFields[key];
    if (value) merged[key] = value;
  }
  return merged;
}

export type HecSrcCampaignFields = Partial<Record<HecSrcCampaignKey, string>>;

export type HecSrcFields = HecSrcCampaignFields & {
  /** Full external referrer URL of the entry pageview, when there was one. */
  ref?: string;
  /** Epoch-ms of the last VALUE capture/overwrite (not of expiry refreshes). */
  ts: number;
};

/** Per-field raw-value cap (chars, pre-encoding). */
export const HEC_SRC_MAX_FIELD_LENGTH = 256;
/**
 * Soft cap on the serialized value; stays under the 1150-char SFCC cookie
 * warning threshold that `linktrust.server.ts` guards against.
 */
export const HEC_SRC_MAX_VALUE_LENGTH = 1100;

/** Last two hostname labels — all our storefront domains are two-label. */
function registrableDomain(hostname: string): string {
  const labels = hostname.toLowerCase().split(".").filter(Boolean);
  return labels.slice(-2).join(".");
}

/**
 * Registrable domains of our own storefronts, derived from `IMAGE_HOSTS`
 * (the 9 storefront origins: 3 brands × dev/stg/prod). A referrer from any of
 * these — including apex, `stg.`, and SFRA-era subdomains like
 * `tpa.fsastore.com` — is internal traffic, not an acquisition source.
 * Consequence (documented in the handoff spec): cross-brand referrals
 * (fsastore→hsastore) are treated internal and not captured.
 */
export const HEC_SRC_OWN_DOMAINS: readonly string[] = [
  ...new Set(
    Object.values(IMAGE_HOSTS)
      .flatMap(byInstance => Object.values(byInstance))
      .map(origin => {
        try {
          return registrableDomain(new URL(origin).hostname);
        } catch {
          return "";
        }
      })
      .filter(Boolean),
  ),
].sort();

/**
 * Is `hostname` one of ours, relative to the host serving the request? The
 * current request host (and anything sharing its registrable domain — preview
 * hosts) always counts as own, on top of the static storefront list. An empty
 * hostname counts as own so callers skip capture rather than storing junk.
 */
export function isOwnHostname(
  hostname: string,
  requestHostname: string,
): boolean {
  const host = hostname.toLowerCase();
  const requestHost = requestHostname.toLowerCase();
  if (!host || host === requestHost) return true;
  const domain = registrableDomain(host);
  return (
    domain === registrableDomain(requestHost) ||
    HEC_SRC_OWN_DOMAINS.includes(domain)
  );
}

/**
 * Serialize fields to the wire value. Field values are capped at
 * `HEC_SRC_MAX_FIELD_LENGTH` chars pre-encoding; when the whole value exceeds
 * `HEC_SRC_MAX_VALUE_LENGTH`, `ref` degrades to its origin, then drops
 * entirely (campaign fields always win — they are the re-supply payload).
 * Built by hand, NOT via `URLSearchParams.toString()`, which would emit
 * form-encoded `+` for spaces.
 */
export function serializeHecSrcValue(fields: HecSrcFields): string {
  const build = (ref: string | undefined): string => {
    const pairs: string[] = [];
    for (const key of HEC_SRC_CAMPAIGN_KEYS) {
      const value = fields[key];
      if (value) {
        pairs.push(
          `${key}=${encodeURIComponent(value.slice(0, HEC_SRC_MAX_FIELD_LENGTH))}`,
        );
      }
    }
    if (ref) {
      pairs.push(
        `ref=${encodeURIComponent(ref.slice(0, HEC_SRC_MAX_FIELD_LENGTH))}`,
      );
    }
    pairs.push(`ts=${Math.floor(fields.ts)}`);
    return pairs.join("&");
  };

  let value = build(fields.ref);
  if (value.length > HEC_SRC_MAX_VALUE_LENGTH && fields.ref) {
    let origin: string | undefined;
    try {
      origin = new URL(fields.ref).origin;
    } catch {
      origin = undefined;
    }
    value = build(origin);
    if (value.length > HEC_SRC_MAX_VALUE_LENGTH) value = build(undefined);
  }
  return value;
}

/**
 * Parse a wire value back to fields. Returns null when the value is corrupt:
 * undecodable pairs, missing/invalid `ts`, or no source signal at all (neither
 * a campaign field nor `ref`). Unknown keys are tolerated (dropped) so a
 * future field addition doesn't invalidate cookies written by newer code.
 */
export function parseHecSrcValue(
  raw: string | null | undefined,
): HecSrcFields | null {
  if (!raw) return null;
  const out: Record<string, string> = {};
  for (const pair of raw.split("&")) {
    const eq = pair.indexOf("=");
    if (eq <= 0) continue;
    try {
      out[pair.slice(0, eq)] = decodeURIComponent(pair.slice(eq + 1));
    } catch {
      return null;
    }
  }
  const ts = Number(out.ts);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  const fields: HecSrcFields = { ts };
  for (const key of HEC_SRC_CAMPAIGN_KEYS) {
    if (out[key]) fields[key] = out[key];
  }
  if (out.ref) fields.ref = out.ref;
  if (!fields.ref && !HEC_SRC_CAMPAIGN_KEYS.some(key => fields[key])) {
    return null;
  }
  return fields;
}

/**
 * Campaign-field equality (ignores `ref`/`ts`) — decides refresh vs overwrite.
 * Compared at the stored truncation length so an over-cap value doesn't churn
 * a fresh `ts` on every hit against its own truncated copy.
 */
export function campaignFieldsEqual(
  a: HecSrcCampaignFields,
  b: HecSrcCampaignFields,
): boolean {
  return HEC_SRC_CAMPAIGN_KEYS.every(
    key =>
      (a[key] ?? "").slice(0, HEC_SRC_MAX_FIELD_LENGTH) ===
      (b[key] ?? "").slice(0, HEC_SRC_MAX_FIELD_LENGTH),
  );
}
